// src/services/surchargeApiSlice.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithRefreshTokenHandler } from '../authFetchBaseQuery';
import {
    ISaveablePricingModel,IMerchantAccount, PaginationSearchSortingType, PricingModelRulesType, ICreatePricingModelRequest, IMerchantPaymentMethod,
    IMerchantPaymentMethodRequest, IPricingModelRequest
} from '../../types/types';
import {
    API_POST_SPLIT_CONFIGURATIONS_LIST, API_POST_SPLIT_CONFIGURATION_DETAILS, API_POST_SPLIT_CONFIGURATION_DELETE,
    API_POST_SPLIT_CONFIGURATIONS_MERCHANT_ACCOUNT_LIST, API_POST_SPLIT_CONFIGURATIONS_CREATE,
    API_GET_MERCHANT_PAYMENT_METHOD_LIST, API_GET_PRICING_MODEL_DETAILS, API_POST_SPLIT_CONFIGURATIONS_UPDATE
} from '../ApiConstants';

/**
 * RTK Query API slice for managing pricing model related endpoints.
 * Handles queries and mutations for pricing model CRUD operations.
 */
export const perseusPayApi = createApi({
    reducerPath: 'perseusPayApi',
    baseQuery: baseQueryWithRefreshTokenHandler,
    tagTypes: ['SplitConfigurations',
        'SplitConfigurationDetails',
        'MerchantAccountsList',
        'CreateSplitConfigurations',
        'MerchantPaymentMethodsList',
    'PricingModelDetails'],
    refetchOnMountOrArgChange: true,
    keepUnusedDataFor: 0,
    endpoints: (builder) => ({

        /**
         * Fetches a list of Perseus Pay Pricing models.
         * @param request The request object containing filter and pagination.
         * @returns List of onboarded dealers.
         */
        getPerseusPaySplitConfigurations: builder.query({
            query: (request: PaginationSearchSortingType) => {
                        return {
                            url: API_POST_SPLIT_CONFIGURATIONS_LIST,
                            method: 'POST',
                            body: JSON.stringify(request),
                        };
                    },            
            providesTags: ['SplitConfigurations'],
        }),

        /**
         * Fetches a list of Perseus Pay Pricing models detail and rules.
         * @param request The request object containing filter and pagination.
         * @returns List of onboarded dealers.
         */
        getSplitConfigDetails: builder.query({
            query: (request: PricingModelRulesType) => {
                return {
                    url: API_POST_SPLIT_CONFIGURATION_DETAILS,
                    method: 'POST',
                    body: JSON.stringify(request),
                };
            },
            providesTags: ['SplitConfigurationDetails'],
        }),


        deleteSplitConfigProfile: builder.mutation<any, PricingModelRulesType>({
            query: (request: PricingModelRulesType) => {
                return {
                    url: API_POST_SPLIT_CONFIGURATION_DELETE,
                    method: 'DELETE',
                    body: JSON.stringify(request)
                }
            }
        }),

        /**
         * Fetches a list of Perseus Pay Pricing models detail and rules.
         * @param request The request object containing filter and pagination.
         * @returns List of onboarded dealers.
         */
        getMerchantAccountsList: builder.query<IMerchantAccount[], void>({
            query: () => ({
                url: API_POST_SPLIT_CONFIGURATIONS_MERCHANT_ACCOUNT_LIST,
                method: 'GET',
            }),
            providesTags: ['MerchantAccountsList'],
        }),

        /**
          * Submit a new ABC model to the server
          */
        createSplitConfigurationProfile: builder.mutation<any, ICreatePricingModelRequest>({
            query: (request: ICreatePricingModelRequest) => ({
                url: API_POST_SPLIT_CONFIGURATIONS_CREATE,       
                method: 'POST',
                body: request,                 // RTKQ automatically serializes to JSON
            }),
            invalidatesTags: ['CreateSplitConfigurations'],       // optional: re-fetch other data after creation
        }),    


        /**
          * Submit a new ABC model to the server
          */
        updateSplitConfigurationProfile: builder.mutation<any, ICreatePricingModelRequest>({
            query: (request: ICreatePricingModelRequest) => ({
                url: API_POST_SPLIT_CONFIGURATIONS_UPDATE,
                method: 'POST',
                body: request,                 // RTKQ automatically serializes to JSON
            }),
            invalidatesTags: ['CreateSplitConfigurations'],       // optional: re-fetch other data after creation
        }),    

        /**
        * Fetches a list of Perseus Pay Pricing models detail and rules.
        * @param request The request object containing filter and pagination.
        * @returns List of onboarded dealers.
        */
        getMerchantPaymentMethodList: builder.query<IMerchantPaymentMethod[], IMerchantPaymentMethodRequest>({
            query: (request: IMerchantPaymentMethodRequest) => ({
                url: API_GET_MERCHANT_PAYMENT_METHOD_LIST,
                method: 'POST',
                body: request,                 // RTKQ automatically serializes to JSON
            }),
            providesTags: ['MerchantPaymentMethodsList'],
        }),

        /**
       * Fetches a list of Perseus Pay Pricing models detail and rules.
       * @param request The request object containing filter and pagination.
       * @returns List of onboarded dealers.
       */
        getPricingModelDetails: builder.query<any, IPricingModelRequest>({
            query: (request: IPricingModelRequest) => ({
                url: API_GET_PRICING_MODEL_DETAILS,
                method: 'POST',
                body: request,                 // RTKQ automatically serializes to JSON
            }),
            providesTags: ['PricingModelDetails'],
        }),

    }),
});

// Export hooks generated by RTK Query for component usage
export const {
    useGetPerseusPaySplitConfigurationsQuery,
    useGetSplitConfigDetailsQuery,
    useLazyGetSplitConfigDetailsQuery,
    useDeleteSplitConfigProfileMutation,
    useLazyGetMerchantAccountsListQuery,
    useCreateSplitConfigurationProfileMutation,
    useLazyGetMerchantPaymentMethodListQuery,
    useGetPricingModelDetailsQuery,
    useUpdateSplitConfigurationProfileMutation
} = perseusPayApi;
